import React from "react";
import { Form } from "antd";
import { IOrganisationIndexCategory } from "../../model/OrganisationIndexCategory";
import { IDocumentType } from "../../../documentTypes/model/DocumentType";
import DataGrid from "../../../core/ui/components/grid/DataGrid";
import DataGridConfigurationBuilder from "../../../core/ui/components/grid/DataGridConfigurationBuilder";
import LegalViewTypeCategoryAssignationForm from "./LegalViewTypeCategoryAssignationForm";
import { PlusCircleFilled } from "@ant-design/icons";
import CreateModal from "../../../core/ui/components/grid/CreateModal";
import OrganisationDocumentTypeForm from "./OrganisationDocumentTypeForm";
import { DocumentStatus } from "../../../core/common/Constants";
import { IVisibilityLevel } from "../../../documentTypes/model/VisibilityLevel";
interface OrganisationLegalViewTypeGridProps {
  translate: any;
  organisationId: number;
  organisationLegalViewTypes: IDocumentType[];
  referenceLoader?: boolean;
  onChangeAddElementsModalVisible: (value: boolean, id?: number) => void;
  addLegalViewTypeToOrganisation;
  setOrganisationLegalViewTypeData;
  legalViewTypes: IDocumentType[];
  onChangeAddTypesToOrganisationModalVisible;
  assignTypesToOrganisationModalVisible: boolean;
  modalVisible: boolean;
  currentLegalViewTypeId?: number;
  legalViewTypeIndexCategories: any;
  addLegalViewTypeIndexCategories;
  organisationIndexCategories: IOrganisationIndexCategory[];
  removeDocumentLegalType: (id: number) => void;
}
const OrganisationLegalViewTypeGrid: React.FC<OrganisationLegalViewTypeGridProps> =
  ({
    translate,
    organisationLegalViewTypes,
    onChangeAddElementsModalVisible,
    addLegalViewTypeToOrganisation,
    setOrganisationLegalViewTypeData,
    legalViewTypes,
    modalVisible,
    currentLegalViewTypeId,
    organisationIndexCategories,
    addLegalViewTypeIndexCategories,
    legalViewTypeIndexCategories,
    organisationId,
    removeDocumentLegalType
  }) => {
    const builder = DataGridConfigurationBuilder({
      translate,
      resourcePrefix: "editors.legal_view_types.fields"
    });
    const gevVisibilityLevels = (
      visibilityLevels: IVisibilityLevel[],
      status: string
    ) => {
      return visibilityLevels?.find((item) => item.status === status)
        ?.visibilityLevel;
    };

    const columns = builder.buildColumns(
      "name",
      {
        name: `${DocumentStatus.IN_PROGRESS}`,
        sortable: false,
        align: "center",
        render: (record) => (
          <span>
            {translate(
              `editors.visibility_levels.${gevVisibilityLevels(
                record.visibilityLevels,
                DocumentStatus.IN_PROGRESS
              )}`
            )}
          </span>
        )
      },
      {
        name: `${DocumentStatus.RECORDED}`,
        sortable: false,
        align: "center",
        render: (record) => (
          <span>
            {translate(
              `editors.visibility_levels.${gevVisibilityLevels(
                record.visibilityLevels,
                DocumentStatus.RECORDED
              )}`
            )}
          </span>
        )
      },
      {
        name: `${DocumentStatus.VERIFIED}`,
        sortable: false,
        align: "center",
        render: (record) => (
          <span>
            {translate(
              `editors.visibility_levels.${gevVisibilityLevels(
                record.visibilityLevels,
                DocumentStatus.VERIFIED
              )}`
            )}
          </span>
        )
      },
      {
        name: `${DocumentStatus.PUBLISHED}`,
        sortable: false,
        align: "center",
        render: (record) => (
          <span>
            {translate(
              `editors.visibility_levels.${gevVisibilityLevels(
                record.visibilityLevels,
                DocumentStatus.PUBLISHED
              )}`
            )}
          </span>
        )
      },
      {
        name: `${DocumentStatus.ARCHIVED}`,
        sortable: false,
        align: "center",
        render: (record) => (
          <span>
            {translate(
              `editors.visibility_levels.${gevVisibilityLevels(
                record.visibilityLevels,
                DocumentStatus.ARCHIVED
              )}`
            )}
          </span>
        )
      },
      {
        name: `${DocumentStatus.INVALIDATED}`,
        sortable: false,
        align: "center",
        render: (record) => (
          <span>
            {translate(
              `editors.visibility_levels.${gevVisibilityLevels(
                record.visibilityLevels,
                DocumentStatus.INVALIDATED
              )}`
            )}
          </span>
        )
      },
      {
        name: "",
        sortable: false,
        align: "center",
        render: (record) => (
          <div
            onClick={() => onChangeAddElementsModalVisible(true, record.id)}
            style={{
              marginLeft: "0.75em",
              marginTop: "0.15em",
              cursor: "pointer"
            }}
            title={translate(
              "editors.legal_view_types_index_organisations.legal_view_types_index_organisations"
            )}
          >
            <PlusCircleFilled style={{ fontSize: "120%" }} />
          </div>
        )
      }
    );

    const [CreateForm] = Form.useForm();
    const [UpdateForm] = Form.useForm();

    return (
      <div>
        <CreateModal
          translate={translate}
          title={translate(
            "editors.legal_view_types_index_organisations.legal_view_types_index_organisations"
          )}
          visible={modalVisible}
          onCancel={() => {
            onChangeAddElementsModalVisible(false);
            CreateForm.resetFields();
          }}
          onCreate={addLegalViewTypeIndexCategories}
          Form={
            <LegalViewTypeCategoryAssignationForm
              translate={translate}
              form={CreateForm}
              currentLegalViewTypeId={currentLegalViewTypeId}
              organisationIndexCategories={organisationIndexCategories}
            />
          }
          formInstance={CreateForm}
          rowData={{ fields: legalViewTypeIndexCategories }}
          className={"modal-width"}
          okText={translate("data_grid.modal.actions.save")}
          cancelText={translate("data_grid.modal.actions.cancel")}
        />
        <DataGrid
          title={translate("editors.legal_view_types.title")}
          columns={columns}
          translate={translate}
          dataSource={organisationLegalViewTypes || []}
          allowCreate={true}
          allowUpdate={true}
          addButtonText={translate("editors.legal_view_types.add_new_text")}
          createForm={
            <OrganisationDocumentTypeForm
              translate={translate}
              form={CreateForm}
              documentTypes={legalViewTypes}
              organisationId={organisationId}
              placeholder={translate(
                "editors.legal_view_types_index_organisations.select_document_type"
              )}
            />
          }
          updateForm={
            <OrganisationDocumentTypeForm
              translate={translate}
              form={UpdateForm}
              documentTypes={legalViewTypes}
              organisationId={organisationId}
              placeholder={translate(
                "editors.legal_view_types_index_organisations.select_document_type"
              )}
            />
          }
          createModalTitle={`${translate(
            "editors.legal_view_types_index_organisations.adding_legal_view_type_to_organisation"
          )}`}
          updateModalTitle={`${translate(
            "editors.legal_view_types_index_organisations.edit_legal_view_type_to_organisation"
          )}`}
          deleteModalTitle={`${translate(
            "editors.legal_view_types_index_organisations.remove_legal_view_type_to_organisation"
          )}`}
          deleteModalMessage={`${translate(
            "editors.legal_view_types_index_organisations.remove_text"
          )}`}
          allowDelete={true}
          onDelete={(e) => removeDocumentLegalType(e.id)}
          onCreate={(e) => {
            addLegalViewTypeToOrganisation(e);
          }}
          onUpdate={(e) => {
            addLegalViewTypeToOrganisation(e);
          }}
          booleanColumnKeys={["valid"]}
          createModalClass={"modal-width"}
          updateModalClass={"modal-width"}
          keyProperty={"id"}
          CreateFormInstance={CreateForm}
          UpdateFormInstance={UpdateForm}
          createActionPosition={"topRight"}
        />
      </div>
    );
  };
export default OrganisationLegalViewTypeGrid;
